import {
  mediaList,
  specials,
  typeLabels,
  regions,
  years,
  type Media,
  type MediaType,
  type Region,
  type Special,
} from "@/mocks/media";

export { mediaList, specials, typeLabels, regions, years };
export type { Media, MediaType, Region, Special };

export function getMediaById(id: string): Media | undefined {
  return mediaList.find((item) => item.id === id);
}

export function getMediaByType(type: MediaType): Media[] {
  return mediaList.filter((item) => item.type === type);
}

export function getNewMedia(limit = 6): Media[] {
  return [...mediaList]
    .filter((item) => item.isNew)
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export function getHotMedia(limit = 6): Media[] {
  return [...mediaList]
    .filter((item) => item.isHot)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
}

export function getSpecialById(id: string): Special | undefined {
  return specials.find((item) => item.id === id);
}

export function getSpecialMedia(special: Special): Media[] {
  return special.mediaIds
    .map((id) => getMediaById(id))
    .filter((item): item is Media => Boolean(item));
}

export function getRelatedMedia(media: Media, limit = 6): Media[] {
  const sameType = mediaList.filter(
    (item) => item.id !== media.id && item.type === media.type,
  );
  const sameRegion = sameType.filter((item) => item.region === media.region);
  const rest = sameType.filter((item) => item.region !== media.region);
  return [...sameRegion, ...rest].slice(0, limit);
}

export type SortKey = "updated" | "popularity" | "rating" | "year";

export interface FilterOptions {
  type?: MediaType | "all";
  region?: Region | "all";
  year?: number | "all";
  sort?: SortKey;
}

export function filterMedia(options: FilterOptions): Media[] {
  const { type = "all", region = "all", year = "all", sort = "updated" } = options;
  const result = mediaList.filter((item) => {
    if (type !== "all" && item.type !== type) return false;
    if (region !== "all" && item.region !== region) return false;
    if (year !== "all" && item.year !== year) return false;
    return true;
  });

  return result.sort((a, b) => {
    switch (sort) {
      case "popularity":
        return b.popularity - a.popularity;
      case "rating":
        return b.rating - a.rating;
      case "year":
        return b.year - a.year;
      case "updated":
      default:
        return a.updatedAt < b.updatedAt ? 1 : -1;
    }
  });
}

export function searchMedia(keyword: string): Media[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return mediaList
    .filter((item) => {
      const haystack = [
        item.title,
        item.director,
        ...item.cast,
        ...item.genres,
        item.region,
        typeLabels[item.type],
        String(item.year),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(key);
    })
    .sort((a, b) => b.popularity - a.popularity);
}

export function searchSuggestions(keyword: string, limit = 6): Media[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return mediaList
    .filter((item) => item.title.toLowerCase().includes(key))
    .slice(0, limit);
}

export function getTypeCount(type: MediaType): number {
  return mediaList.filter((item) => item.type === type).length;
}